import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Local expand/collapse state for the inbox list. Mark-read runs when an unread
 * message is dismissed (collapse, switch to another row, or leave the page) —
 * not when it is first opened — so the red dot stays until the host leaves it.
 *
 * `markRead` is held in a ref so an inline callback from the page does not
 * re-fire the unmount cleanup on every parent render (which would mark-read
 * as soon as the row expands and re-renders).
 */
export function useNotificationListInteraction(markRead: (id: string) => void) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const expandedIdRef = useRef<string | null>(null);
  const expandedWasUnreadRef = useRef(false);
  const acknowledgedRef = useRef<Set<string>>(new Set());
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;
  const [, forceRender] = useState(0);

  const dismissIfUnread = useCallback((id: string | null, wasUnread: boolean) => {
    if (!id || !wasUnread) return;
    if (acknowledgedRef.current.has(id)) return;
    acknowledgedRef.current.add(id);
    markReadRef.current(id);
    forceRender((n) => n + 1);
  }, []);

  const handleRowClick = useCallback(
    (id: string, read: boolean) => {
      const prevExpanded = expandedIdRef.current;
      const prevWasUnread = expandedWasUnreadRef.current;
      const nextExpanded = prevExpanded === id ? null : id;

      if (prevExpanded && prevExpanded !== nextExpanded) {
        dismissIfUnread(prevExpanded, prevWasUnread);
      }

      expandedIdRef.current = nextExpanded;
      expandedWasUnreadRef.current = nextExpanded === id ? !read : false;
      setExpandedId(nextExpanded);
    },
    [dismissIfUnread],
  );

  useEffect(() => {
    return () => {
      dismissIfUnread(expandedIdRef.current, expandedWasUnreadRef.current);
    };
  }, [dismissIfUnread]);

  const showUnreadDot = useCallback(
    (id: string, read: boolean) => !read && !acknowledgedRef.current.has(id),
    [],
  );

  return { expandedId, handleRowClick, showUnreadDot };
}
